import React, { useEffect, useState, useContext, useRef } from 'react';
import { AuthContext } from "../contexts/auth";
import CalendarWeekSelect from './calendar_week_select';
import { ChevronDownIcon, ChevronUpIcon, ChevronLeftIcon, ChevronRightIcon, DocumentPlusIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import ComboboxMultiSelectAPI from './combobox_multiselect_autocomplete_api';
import { getCompanyAttendance } from '../services/api_calendar';
import { getEmployeesWithActiveContract } from '../services/api_calendar';
import { getMonthBlocked } from '../services/api_months_blocked';
import { Link } from 'wouter';
import Swal from 'sweetalert2';
import { getJustificationsCompanyList } from '../services/api_justifications';
import { deleteJustificationRequest } from '../services/api_justification_requests';
import EditGiustificativoModal from './modal_edit_giustificativo';
import AddGiustificativoModal from './modal_add_giustificativo';
import SelectGiustificativoModal from './modal_select_giustificativo';
import Fetching from './fetching';
import moment from 'moment';
import DayDetailsModal from './day_details_modal';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';

const CustomTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))({
'& .MuiTooltip-tooltip': {
    backgroundColor: '#a3a3a3',
    color: '#FFFFFF',
    fontSize: '0.8rem',
},
});


const EmployeeAttendanceWeekTable = ({ page, pageSize, setTotalResult, totalResult, setNextPage, setPrevPage, selectedUnit }) => {
    const { loading, token, profile } = useContext(AuthContext);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [currentWeek, setCurrentWeek] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(new Date());
    const [attendanceData, setAttendanceData] = useState(null);
    const [giustificativi, setGiustificativi] = useState([]);
    const [giustificativiMap, setGiustificativiMap] = useState({});
    const [showModalEditGiustificativo, setShowModalEditGiustificativo] = useState(false);
    const [showModalAddGiustificativo, setShowModalAddGiustificativo] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedJustification, setSelectedJustification] = useState([]);
    const [selectedDay, setSelectedDay] = useState(null);
    const [justificationRequests, setJustificationRequests] = useState([]);
    const [justificationId, setJustificationId] = useState(null);
    const [selectedDescription, setSelectedDescription] = useState('');
    const [holidays, setHolidays] = useState([]);
    const tableContainerRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [scrollTop, setScrollTop] = useState(0);
    const firstHeaderRef = useRef(null);
    const [selectedJustificationType, setSelectedJustificationType] = useState('');
    const [justificationsOfType, setJustificationsOfType] = useState('');
    const [showModalJustificationSelect, setShowModalJustificationSelect] = useState(false);
    const [dipendenti, setDipendenti] = useState([]);
    const [modalDetailsData, setModalDetailsData] = useState();
    const [isModalDetailsOpen, setIsModalDetailsOpen] = useState(false);
    const controller = useRef(null);
    const [blockedMonths, setBlockedMonths] = useState([]);

    
    async function fetchActiveEmployees(signal) {
        if (currentWeek.length === 0) return;
    
        try {
            const startDate = formatDateWeekSelected(new Date(currentWeek[0]));
            const endDate = formatDateWeekSelected(new Date(currentWeek[currentWeek.length - 1]));
    
            setIsLoading(true);
            setIsError(false);
            
            const shouldPaginate = selectedEmployees.length === 0;

            const attendanceResponse = await getCompanyAttendance(
                token,
                { startDate, endDate },
                selectedUnit?.id,
                selectedEmployees.map((e) => e.id),
                shouldPaginate ? page : null,
                shouldPaginate ? pageSize : null,
                signal
            );
    
            setNextPage(attendanceResponse.next);
            setPrevPage(attendanceResponse.previous);
            setTotalResult(attendanceResponse.count);
            setAttendanceData(attendanceResponse.results);
    
            const holidaysData = [];
            const newDipendenti = [];
            attendanceResponse.results.forEach((employee) => {
                employee.days.forEach((day) => {
                    if (day.holidays && day.holidays.length > 0) {
                        day.holidays.forEach((holiday) => {
                            holidaysData.push({ date: day.date, name: holiday.name });
                        });
                    }
                });
                newDipendenti.push({
                    first_name: employee.first_name,
                    last_name: employee.last_name,
                    cf: employee.cf,
                    id: employee.id,
                    email_auth: employee.email_auth,
                });
            });
            setDipendenti(newDipendenti);
            setHolidays(holidaysData);
    
            const requiredAttendancePermissions = ['hr.view_attendance_ou', 'hr.view_attendance_all'];
            const hasAttendancePermission =
                profile.customer_type !== 'employee' ||
                requiredAttendancePermissions.some((permission) =>
                    profile.employee.permissions.includes(permission)
                );
    
            const requiredJustificationPermissions = ['hr.manage_justification_all', 'hr.manage_justification_ou'];
            const hasJustificationPermission =
                profile.customer_type !== 'employee' ||
                requiredJustificationPermissions.some((permission) =>
                    profile.employee.permissions.includes(permission)
                );
    
            if (!hasAttendancePermission) {
                attendanceResponse.results.forEach((employee) => {
                    employee.days.forEach((day) => {
                        day.attendance = [];
                    });
                });
                setAttendanceData(attendanceResponse.results);
            }
    
            if (!hasJustificationPermission) {
                attendanceResponse.results.forEach((employee) => {
                    employee.days.forEach((day) => {
                        day.justifications = [];
                    });
                });
                setJustificationRequests([]);
            } else {
                const giustificativiData = await getJustificationsCompanyList(token);
                setGiustificativi(giustificativiData);
                
                const giustificativiMapping = giustificativiData.reduce((acc, curr) => {
                    acc[curr.justification.code] = curr.justification;
                    return acc;
                }, {});
    
                setGiustificativiMap(giustificativiMapping);
                
            }

            const blockedMonthsResponse = await getMonthBlocked(token, { startDate, endDate });
            setBlockedMonths(blockedMonthsResponse);
            
            setIsLoading(false);
        } catch (error) {
            if (error?.name != 'CanceledError') {
                console.error(error);
                setIsError(true);
                setIsLoading(false);
            }
        }
    }
    
    const isDayBlocked = (date) => {       
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        
        return blockedMonths.some(blocked => blocked.year === year && blocked.month === month);
    };    

    useEffect(() => {
        if (controller.current) {
            controller.current.abort();
            setIsLoading(false);
        }
    
        controller.current = new AbortController();
        const signal = controller.current.signal;
    
        fetchActiveEmployees(signal);
    
    }, [token, currentWeek, selectedUnit, selectedEmployees, page, pageSize]);

    useEffect(() => {
        const today = new Date();
        setCurrentWeek(getCompleteWeek(today));
    }, []);

    const getCompleteWeek = (date) => {
        const currentDayOfWeek = date.getDay();
        const mondayDiff = currentDayOfWeek === 0 ? -6 : 1 - currentDayOfWeek;
        const monday = new Date(date);
        monday.setDate(date.getDate() + mondayDiff);
        const weekDays = [monday];
        for (let i = 1; i < 7; i++) {
            const day = new Date(monday);
            day.setDate(monday.getDate() + i);
            weekDays.push(day);
        }
        return weekDays;
    };

    const handleNavigateWeek = (direction) => {
        const newMonday = new Date(currentWeek[0]);
        newMonday.setDate(newMonday.getDate() + 7 * direction);
        setCurrentWeek(getCompleteWeek(newMonday));
        setSelectedMonth(new Date(newMonday.getFullYear(), newMonday.getMonth(), 1));
    };

    const formatDateWeekSelected = (date) => {
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
            console.error('Invalid date provided:', date);
            return 'Invalid Date';
        }

        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleSelectionChange = (newSelectedEmployees) => {
        setSelectedEmployees(newSelectedEmployees);
    };

    const handleDeselectEmployee = (employeeId) => {
        setSelectedEmployees((prevSelected) =>
            prevSelected.filter((emp) => emp.id !== employeeId)
        );
    };

    const handleResetSelection = () => {
        setSelectedEmployees([]);
    };

    useEffect(() => {
        const today = new Date();
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        setFirstDayOfMonth(firstDayOfMonth);

        const currentDayOfWeek = today.getDay();
        const mondayDiff = currentDayOfWeek === 0 ? -6 : 1 - currentDayOfWeek;
        const monday = new Date(today);
        monday.setDate(today.getDate() + mondayDiff);
        const weekDays = [monday];
        for (let i = 1; i < 7; i++) {
            const day = new Date(monday);
            day.setDate(monday.getDate() + i);
            weekDays.push(day);
        }
        setCurrentWeek(weekDays);
    }, []);

    const [firstDayOfMonth, setFirstDayOfMonth] = useState(new Date());
    const currentMonthName = firstDayOfMonth.toLocaleString('it-IT', { month: 'long' }).toLowerCase();

    const getWeekRange = (monday, sunday) => {
        const startDay = monday.getDate();
        const endDay = sunday.getDate();
    
        const startMonth = monday.toLocaleDateString('it-IT', { month: 'long' });
        const endMonth = sunday.toLocaleDateString('it-IT', { month: 'long' });
    
        if (startMonth !== endMonth) {
            return `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
        }
    
        return `${startDay} - ${endDay} ${startMonth}`;
    };
    
    const formatDay = (date) => {
        if (!date) return "";

        const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString().split('T')[0];

        const dayOfWeek = date.getDay();
        const formattedDate = date.toISOString().split('T')[0];

        const isHoliday = holidays.some(holiday => holiday.date === localDate);
    
        const options = { weekday: 'long', day: 'numeric' };
    
        if (isHoliday) {
            return (
                <div className="text-orange-500 font-bold">
                    {date.toLocaleDateString('it-IT', options)}
                </div>
            );
        } else if (dayOfWeek === 0) {

            return (
                <div className="text-emerald-500 font-bold">
                    {date.toLocaleDateString('it-IT', options)}
                </div>
            );
        } else {
            return (
                <div className="text-gray-600">
                    {date.toLocaleDateString('it-IT', options)}
                </div>
            );
        }
    };
    

    const handleWeekSelect = (weekDays) => {
        setCurrentWeek(weekDays);

        // Aggiorna il mese selezionato in base al primo giorno della settimana selezionata
        if (weekDays.length > 0) {
            const firstDayOfWeek = weekDays[0];
            setSelectedMonth(new Date(firstDayOfWeek.getFullYear(), firstDayOfWeek.getMonth(), 1));
        }
    };

    const [calendarVisible, setCalendarVisible] = useState(false);

    const handleSelectClick = () => {
        setCalendarVisible(!calendarVisible);
    };

    const getEmptyCellsAtStart = (firstDayOfWeek) => {
        if (firstDayOfWeek instanceof Date) {
            const startDayOfWeek = firstDayOfWeek.getDay();
            return startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;
        } else {
            return 0;
        }
    };

    const getEmptyCellsAtEnd = (lastDayOfWeek) => {
        if (lastDayOfWeek instanceof Date) {
            const endDayOfWeek = lastDayOfWeek.getDay();
            return endDayOfWeek === 0 ? 0 : 7 - endDayOfWeek;
        } else {
            return 0;
        }
    };
    
    const emptyCellsAtStart = getEmptyCellsAtStart(currentWeek[0]);
    const emptyCellsAtEnd = getEmptyCellsAtEnd(currentWeek[currentWeek.length - 1]);

    const openModal = (selectedRequest) => {
        setSelectedRequest(selectedRequest);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
    };

    const handleShowJustificationsOfType = (type, justifications, description, dipendente, dayData) => {
        setSelectedJustificationType(type);
        setSelectedDescription(description);
        setJustificationsOfType(justifications);
        setShowModalJustificationSelect(true);
        setSelectedEmployee(dipendente);
        setSelectedJustification(dayData);
    };

    const openModalEditGiustificativi = (justification, dipendente, dayData, justificationId) => {
        setSelectedJustification(dayData);      
        setSelectedDescription(justification);       
        setSelectedEmployee(dipendente);
        setShowModalEditGiustificativo(true);
        setJustificationId(justificationId);
    };

    const openModalAddGiustificativo = (dipendente, formattedDay, attendanceForDay) => {
        setShowModalAddGiustificativo(true);
        setSelectedEmployee(dipendente);
        setSelectedDay(formattedDay);
        setSelectedJustification(attendanceForDay);
    };

    const handleJustificationSelected = (descriptionJustification, dipendente, dayData, justificationId, status) => {
        if (status === 'null' || status !== 'approved') {
            Swal.fire({
                html: `
                    <p style="padding-top: 10px;"><strong>Richiesta in attesa di esito.<br/> Vai alle </strong><a href="/pending_justifications" style="color: #7c3aed; text-decoration: underline;">richieste da gestire</a></p>
                `,
            });
            return;
        }

        openModalEditGiustificativi(descriptionJustification, dipendente, dayData, justificationId);
    };

    const handleUpdateJustification = async ({ dayData, justificationId, startISO, endISO, note, employee }) => {
        try {
            const updatedJustification = {
                ...dayData,
                id: justificationId,
                datetime_from: startISO,
                datetime_to: endISO,
                note,
            };
            setSelectedJustification(updatedJustification);
    
            await fetchActiveEmployees();
    
        } catch (error) {
            console.error('Errore durante l\'aggiornamento del giustificativo:', error);
        }
    };

    const handleDeleteRequest = async (justificationId, employeeId, selectedJustification) => {
        const dateFrom = moment(selectedJustification.datetime_from).format('DD/MM/YYYY');
        const dateTo = moment(selectedJustification.datetime_to).format('DD/MM/YYYY');
    
        
        const confirmDelete = await Swal.fire({
            icon: 'warning',
            title: 'Conferma eliminazione',
            text: 'Stai eliminando un giustificativo approvato. L\'azione non è reversibile. Confermi di voler eliminare?',
            showCancelButton: true,
            confirmButtonText: 'Conferma',
            cancelButtonText: 'Annulla',
            cancelButtonColor: '#d1d5db',
        });

        if (!confirmDelete.isConfirmed) {
            return;
        }

        setIsLoading(true);
        setIsError(false);

        try {
            await deleteJustificationRequest(token, justificationId);

            const updatedJustificationRequests = justificationRequests.filter(j => j.justification.id !== justificationId);
            setJustificationRequests(updatedJustificationRequests);

            await fetchActiveEmployees();

            setShowModalEditGiustificativo(false);

            Swal.fire({
                icon: 'success',
                title: 'Richiesta eliminata!',
                text: 'La richiesta è stata eliminata con successo!',
                showConfirmButton: false,
                timer: 1000,
            });

        } catch (error) {
            console.error('Errore durante l\'eliminazione del giustificativo:', error);
            setIsError(true);
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Si è verificato un errore durante l\'eliminazione del giustificativo. Per favore riprova.',
                confirmButtonText: 'OK',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleReject = async (newJustificationRequest) => {
        const startDate = formatDateWeekSelected(new Date(currentWeek[0]));
        const endDate = formatDateWeekSelected(new Date(currentWeek[currentWeek.length - 1]));

        try {
            const updatedJustificationRequests = [...justificationRequests, newJustificationRequest];
            setJustificationRequests(updatedJustificationRequests);
    
            setShowModalAddGiustificativo(false);
    
            await fetchActiveEmployees();
    
        } catch (error) {
            console.error('Errore durante l\'aggiornamento della richiesta', error);
        }
    };
    

    const handleNewJustificationRequest = async (newJustificationRequest, employeeId) => {
        const startDate = formatDateWeekSelected(new Date(currentWeek[0]));
        const endDate = formatDateWeekSelected(new Date(currentWeek[currentWeek.length - 1]));

        try {
            const updatedJustificationRequests = [...justificationRequests, newJustificationRequest];
            setJustificationRequests(updatedJustificationRequests);
    
            setShowModalAddGiustificativo(false);
    
            await fetchActiveEmployees();
    
        } catch (error) {
            console.error('Errore durante l\'aggiunta del giustificativo:', error);
        }
    };
    
    // const handleSwalMessage = (employeeId, hours, justifications, day) => {
    //     if (!employeeId) {
    //         return;
    //     }
    
    //     const formattedDay = `${day.getDate()}/${day.getMonth() + 1}/${day.getFullYear()}`;
    
    //     let justificationHtml = '';
    //     const approvedJustifications = justifications.filter(j => j.status === 'approved');
    //     const pendingJustifications = justifications.filter(j => j.status === null);       
    
    //     if (approvedJustifications.length > 0) {
    //         justificationHtml += `
    //             <p style="padding-top: 10px;"><strong>Ore Giustificativi</strong></p>
    //             <ul>
    //                 ${approvedJustifications.map((justification) => {
    //                     let hours = (justification.duration / 60).toFixed(2);
    //                     if (hours.endsWith('.00')) {
    //                         hours = parseFloat(hours).toFixed(0);
    //                     }
    //                     const description = justification.description || justification.code;
    //                     return `<li>${description}: ${hours}</li>`;
    //                 }).join('')}
    //             </ul>
    //         `;
    //     }
    
    //     if (pendingJustifications.length > 0) {
    //         justificationHtml += `
    //             <p style="padding-top: 10px;"><strong>Richiesta in attesa di esito.<br/> Vai alle </strong><a href="/pending_justifications" style="color: #7c3aed; text-decoration: underline;">richieste da gestire</a></p>
    //         `;
    //     }
    
    //     const formattedDate = `${day.getFullYear()}-${(day.getMonth() + 1).toString().padStart(2, '0')}-${day.getDate().toString().padStart(2, '0')}`;
    //     const holidaysOnDay = holidays.filter(h => h.date === formattedDate);
    
    //     let holidayNamesSet = new Set();
    //     holidaysOnDay.forEach(holiday => {
    //         holidayNamesSet.add(holiday.name);
    //     });
    //     const uniqueHolidayNames = Array.from(holidayNamesSet);
    
    //     let holidayHtml = '';
    //     if (uniqueHolidayNames.length > 0) {
    //         holidayHtml = `<p style="padding-bottom: 10px;"><strong>Festività:</strong> ${uniqueHolidayNames.join(', ')}</p>`;
    //     }
    
    //     Swal.fire({
    //         title: `Dettaglio del giorno ${formattedDay}`,
    //         html: `
    //             <div>
    //                 ${holidayHtml}
    //                 <p><strong>Ore Turno:</strong> ${hours}</p>
    //                 ${justificationHtml}
    //             </div>
    //         `,
    //     });
    // };

    const handleOpenDetailsModal = (dipendente, formattedDay) => {
        if (!dipendente) return;

        setSelectedEmployee(dipendente);
        setModalDetailsData(formattedDay);
        setIsModalDetailsOpen(true);
    };
    
    const justificationColors = {
        'FE': '#fef3c7',
        'PE': '#bfdbfe',
        'RO': '#bfdbfe',
        'PD': '#bfdbfe',
        'EO': '#c4b5fd',
        'MAL': '#fecaca',
        'ST': '#fae8ff',
    };

    const getBackgroundColor = (key) => justificationColors[key] || '#f3f4f6';

    const isWeekend = (date) => {
        const dayOfWeek = new Date(date).getDay();
        return dayOfWeek === 0; // 0 = Domenica
    };

    const startDragging = (e) => {
        setIsDragging(true);
        setStartX(e.pageX - tableContainerRef.current.offsetLeft);
        setScrollLeft(tableContainerRef.current.scrollLeft);
    };

    const stopDragging = () => {
        setIsDragging(false);
    };

    const onDrag = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - tableContainerRef.current.offsetLeft;
        const walk = (x - startX) * 2; // Velocità dello scroll
        tableContainerRef.current.scrollLeft = scrollLeft - walk;
    };

    const handleScroll = () => {
        const container = tableContainerRef.current;
        if (container) {
            const scrollTop = container.scrollTop;
            setScrollTop(scrollTop);
        }
    };

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', onDrag);
            document.addEventListener('mouseup', stopDragging);
        } else {
            document.removeEventListener('mousemove', onDrag);
            document.removeEventListener('mouseup', stopDragging);
        }

        const container = tableContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
        }

        return () => {
            document.removeEventListener('mousemove', onDrag);
            document.removeEventListener('mouseup', stopDragging);
            if (container) {
                container.removeEventListener('scroll', handleScroll);
            }
        };
    }, [isDragging]);


    // if(isLoading)
    //     return (
    //         <div className='w-full'>
    //             <Fetching />
    //         </div>
    //     );
    if (isError) return <div>Error loading data</div>;


    return (
        <div className='container mx-4 px-3 rounded-md'>
            <div className="grid grid-cols-1 md:grid-cols-5 mb-4 justify-center items-center">
                <div className="col-span-1 hidden md:block"></div>
                <div className="col-span-1 md:col-span-2 mb-4 items-center justify-center md:ms-5">
                    <ComboboxMultiSelectAPI
                        getData={async (query) => {
                            if (query.trim() === '') {
                            return Promise.resolve([]);
                            }

                            const page = 1;
                            const pageSize = 5;

                            try {
                            const res = await getEmployeesWithActiveContract(token, query, 'hr_active', null, page, pageSize);

                            const employeesArray = Array.isArray(res.results) ? res.results : [];
                            return employeesArray; 
                            } catch (error) {
                            console.error("Errore durante il recupero dei dati:", error);
                            return [];
                            }
                        }}
                        getDisplay={(dip) => dip.last_name && dip.first_name ? `${dip.last_name} ${dip.first_name}` : dip.cf}
                        getId={(dip) => dip.id}
                        selectedValues={selectedEmployees || selectedUnit}
                        onChange={handleSelectionChange}
                        placeholder="Cerca dipendente..."
                    />
                </div>
            </div>
            {selectedEmployees.length > 0 ? (
                <div className="mb-4">
                    <div className="flex flex-wrap gap-2 mt-2">
                        {selectedEmployees.map((employee) => (
                            <div
                                key={employee.id}
                                className="inline-flex items-center px-3 py-1 rounded-full bg-indigo-200 text-indigo-800 text-xs font-medium cursor-pointer hover:bg-indigo-300"
                                onClick={() => handleDeselectEmployee(employee.id)}
                            >
                                {employee?.last_name && employee?.first_name
                                    ? `${employee?.last_name} ${employee?.first_name}`
                                    : employee?.cf}
                                <span className="ml-2 text-gray-600 hover:text-red-500">&times;</span>
                            </div>
                        ))}
                        <button
                            className="inline-flex items-center rounded bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 "
                            onClick={handleResetSelection}
                        >
                            Reset selezione
                        </button>
                    </div>
                </div>
            ) : null}


            <div className='grid grid-cols-1 w-full'>
                <div
                    style={{ flex: '1 1 auto', width: 'auto' }}
                    className="py-5 px-4 border-t border-l border-r border-gray-200 text-center w-24 text-xs font-semibold text-gray-600 uppercase align-middle"
                >
                    <div className='grid  md:grid-cols-5 sm:justify-start md:justify-around grid-cols-3'>
                        <div className='col-span-1 p-2 flex align-center justify-end sm:justify-center md:justify-end me-3'>
                            <button
                                className='text-gray-700 font-semibold px-1 hover:bg-gray-100'
                                onClick={() => handleNavigateWeek(-1)}
                            >
                                <ChevronLeftIcon className='w-5 h-5 cursor-pointer' />
                            </button>
                        </div>
                        <div className='col-span-1 p-2 flex align-center md:justify-end sm:justify-center me-3'>Seleziona Settimana</div>
                        <div className='col-span-1 p-2 align-center justify-start sm:justify-center md:justify-start md:hidden block'>
                            <button
                                className='text-gray-700 font-semibold px-1 hover:bg-gray-100'
                                onClick={() => handleNavigateWeek(1)}
                            >
                                <ChevronRightIcon className='w-5 h-5 cursor-pointer' />
                            </button>
                        </div>
                        <div
                            onClick={handleSelectClick}
                            style={{ cursor: 'pointer' }}
                            className='col-span-3 md:col-span-2 border border-gray-300 rounded-md shadow-md p-2 pl-4 flex items-center md:w-4/5 w-full justify-between'
                        >
                            <div>
                                {currentWeek.length > 0 && `${getWeekRange(currentWeek[0], currentWeek[currentWeek.length - 1])} / ${selectedMonth.getFullYear()}`}
                            </div>
                            {!calendarVisible ? <ChevronDownIcon className='h-4 w-4 bg-indigo-100 rounded-md' /> : <ChevronUpIcon className='h-4 w-4 bg-indigo-100 rounded-md' />}
                        </div>
                        <div className='col-span-1 p-2 align-start justify-start hidden md:block'>
                            <button
                                className='text-gray-700 font-semibold px-1 hover:bg-gray-100 flex items-start'
                                onClick={() => handleNavigateWeek(1)}
                            >
                                <ChevronRightIcon className='w-5 h-5 cursor-pointer' />
                            </button>
                        </div>
                    </div>
                    {calendarVisible &&
                        <CalendarWeekSelect
                            onWeekSelect={handleWeekSelect}
                            onCloseCalendar={handleSelectClick}
                            selectedMonth={selectedMonth}
                        />
                    }
                </div>
            </div>

            <div className="overflow-x-auto" style={{ overflowY: 'scroll', overflowX: 'auto', height: '100%' }}>
                <div className="flex items-center pb-3 pl-3 border-l border-r">
                    <div className="w-3 h-3 bg-emerald-200 rounded-full mr-1"></div>
                    <span className='block font-medium text-gray-700 uppercase text-xs mr-3'>Ore Turno</span>
                    <div className="w-3 h-3 bg-amber-200 rounded-full mr-1"></div>
                    <span className='block font-medium text-gray-700 uppercase text-xs mr-3'>Ferie</span>
                    <div className="w-3 h-3 bg-sky-200 rounded-full mr-1"></div>
                    <span className='block font-medium text-gray-700 uppercase text-xs mr-3'>Permessi/ROL</span>
                    <div className="w-3 h-3 bg-violet-200 rounded-full mr-1"></div>
                    <span className='block font-medium text-gray-700 uppercase text-xs mr-3'>Ex Festività</span>
                    <div className="w-3 h-3 bg-orange-200 rounded-full mr-1"></div>
                    <span className='block font-medium text-gray-700 uppercase text-xs mr-3'>Malattia</span>
                    <div className="w-3 h-3 bg-fuchsia-200 rounded-full mr-1"></div>
                    <span className='block font-medium text-gray-700 uppercase text-xs mr-3'>Straordinari</span>
                    <div className="w-3 h-3 bg-gray-200 rounded-full mr-1"></div>
                    <span className='block font-medium text-gray-700 uppercase text-xs'>Altro</span>
                </div>
                
                <div
                    ref={tableContainerRef}
                    style={{
                        overflowX: 'auto',
                        overflowY: 'auto',
                        cursor: isDragging ? 'grabbing' : 'grab',
                        maxHeight: '70vh',
                        position: 'relative'
                    }}
                    onMouseDown={startDragging}
                >
                    {isLoading ? (
                        <Fetching />
                    ) : (
                        <table className="min-w-full border divide-y divide-gray-200">
                            <thead ref={firstHeaderRef} style={{ position: 'sticky', top: 0, zIndex: 2, background: 'white' }}>
                                <tr>
                                    <th
                                        style={{
                                            position: 'sticky',
                                            left: 0,
                                            background: 'white',
                                            zIndex: 3,
                                            border: '2px solid #e5e7eb',
                                            minWidth: '7rem',
                                        }}
                                        className="pl-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase"
                                    >
                                        Dipendenti
                                    </th>
                                    {Array.from({ length: emptyCellsAtStart }).map((_, index) => (
                                        <th key={`empty-start-${index}`} className="py-3 px-4 border-2 border-gray-200 w-24 bg-gray-50"></th>
                                    ))}
                                    {currentWeek.map((day, index) => (
                                        <th key={index} className="py-3 px-4 border-2 border-gray-200 text-center w-24 text-xs bg-white font-semibold uppercase">
                                            {formatDay(day)}
                                        </th>
                                    ))}
                                    {Array.from({ length: emptyCellsAtEnd }).map((_, index) => (
                                        <th key={`empty-end-${index}`} className="py-3 px-4 border-2 border-gray-200 w-24 bg-gray-50"></th>
                                    ))}
                                    <th
                                        className="text-xs font-semibold text-gray-600 uppercase bg-gray-50 border-2 border-gray-200"
                                        style={{
                                            position: 'sticky',
                                            right: 0,
                                            background: 'white',
                                            zIndex: 2,
                                            border: '2px solid #e5e7eb',
                                            width: '20%',
                                        }}
                                    >
                                        Totali
                                    </th>
                                </tr>
                            </thead>

                            <tbody className="bg-white divide-y divide-gray-200">
                                {(() => {
                                    const employeesToShow = selectedEmployees.length > 0 ? selectedEmployees : dipendenti;

                                    if (!isLoading && employeesToShow.length === 0) {
                                        return (
                                            <tr>
                                                <td colSpan={9} className="pl-4 py-3 border-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase">Non sono presenti anagrafiche</td>
                                            </tr>
                                        );
                                    }

                                    return employeesToShow
                                        .slice()
                                        .sort((a, b) => {
                                            const aValue = a.last_name || a.cf;
                                            const bValue = b.last_name || b.cf;
                                            return aValue.localeCompare(bValue);
                                        })
                                        .map((dipendente) => {
                                            const employeeAttendance = attendanceData?.find((employee) => employee.id === dipendente.id)?.days || [];
                                            let totalPresenceHours = 0;
                                            let totalPresenceActualHours = 0;
                                            const justificationTotals = {};

                                            const employeeData = attendanceData?.find((employee) => employee.id === dipendente.id);
                                            
                                            
                                            const allJustificationTypes = new Set();

                                            currentWeek.forEach((day) => {
                                                const formattedDay = formatDateWeekSelected(day);
                                                employeesToShow.forEach((dipendente) => {
                                                    const attendanceForDay = employeeAttendance;
                                                    const dayData = attendanceForDay.find((attendance) => attendance.date === formattedDay);
                                                    if (dayData) {
                                                        (dayData.justifications || []).forEach((justification) => {
                                                            allJustificationTypes.add(justification.code);
                                                        });
                                                    }
                                                });
                                            });

                                            currentWeek.forEach((day) => {
                                                const formattedDay = formatDateWeekSelected(day);
                                                const attendanceForDay = employeeAttendance.find((attendance) => attendance.date === formattedDay);
                                                
                                                // Calcolo delle ore di presenza totali
                                                if (attendanceForDay && attendanceForDay.attendance_expected) {
                                                    totalPresenceHours += attendanceForDay.attendance_expected / 60;
                                                }

                                                // Calcolo delle ore effettive di presenza totali
                                                if (attendanceForDay && attendanceForDay.attendance_actual) {
                                                    totalPresenceActualHours += attendanceForDay.attendance_actual / 60;
                                                }
                            
                                                // Calcolo dei totali per ogni tipo di giustificativo
                                                (attendanceForDay?.justifications || []).forEach((justification) => {
                                                    const hours = justification.duration / 60;
                                                    justificationTotals[justification.code] = (justificationTotals[justification.code] || 0) + hours;
                                                });
                                            });
                            
                                            // Formatta i risultati dei totali dei giustificativi
                                            const formattedJustificationTotals = Object.entries(justificationTotals).map(([code, hours]) => {
                                                const formattedHours = Number.isInteger(hours) ? hours.toFixed(0) : hours.toFixed(2);
                                                const description = giustificativiMap[code]?.description || code;
                                                return { code, hours: formattedHours, description };
                                            });

                                            const justificationOrder = Array.from(allJustificationTypes);

                                            return (
                                                <tr key={dipendente.id}>
                                                    <td
                                                        style={{
                                                            position: 'sticky',
                                                            left: 0,
                                                            background: 'white',
                                                            zIndex: 1,
                                                            border: '2px solid #e5e7eb',
                                                            minWidth: '7rem',
                                                        }}
                                                        className="px-2 py-3 border-2 border-gray-200 text-left text-xs bg-white font-semibold text-gray-600 uppercase w-28"
                                                        title={dipendente.first_name ? `${dipendente.last_name} ${dipendente.first_name}` : dipendente.cf}
                                                    >
                                                        <Link href={`/dipendenti/${dipendente.cf}`} className="hover:text-indigo-700">
                                                            {dipendente.first_name ? `${dipendente.last_name} ${dipendente.first_name}`.slice(0, 25) : dipendente.cf}
                                                            {dipendente.first_name && `${dipendente.last_name} ${dipendente.first_name}`.length > 25 ? '...' : ''}
                                                        </Link>
                                                    </td>
                                                    {Array.from({ length: emptyCellsAtStart }).map((_, index) => (
                                                        <td key={`empty-start-${index}`} className="py-3 px-4 border-2 border-gray-200 w-24 bg-gray-50"></td>
                                                    ))}
                                                    {currentWeek.map((day, i) => {
                                                        const formattedDay = formatDateWeekSelected(day);
                                                        const attendanceForDay = employeeAttendance.find((attendance) => attendance.date === formattedDay);

                                                        const weekHours = attendanceForDay ? attendanceForDay.week_hours : null;
                                                        const shiftType = attendanceForDay ? attendanceForDay.shift_type : null;
                                            
                                    
                                                        let canAddJustification = (
                                                            shiftType === 'a_chiamata'
                                                            || (shiftType === 'turno' && weekHours && weekHours > 0)
                                                            || (shiftType === 'fisso' && weekHours && weekHours > 0)
                                                        );
                                                        
                                                        const hasActiveContract = attendanceForDay?.has_active_contract === true;
                                                        let hours = '-';
                                                        let justifications = [];

                                                        if (attendanceForDay && attendanceForDay.shift_type !== null && (attendanceForDay.week_hours || attendanceForDay.shift_type === 'a_chiamata')) {
                                                            const attendanceHours = attendanceForDay.attendance_expected / 60;
                                                            hours = Number.isInteger(attendanceHours) ? attendanceHours.toFixed(0) : attendanceHours.toFixed(2);
                                                            
                                                            justifications = attendanceForDay.justifications || [];
                                                        }
                                                        
                                                        const isHoliday = holidays.some(holiday => holiday.date === formattedDay);
                                                        const weekend = isWeekend(day);

                                                        const orderedJustifications = justificationOrder.map((type) => {
                                                            return justifications.filter(justification => justification.code === type);
                                                        });

                                                        const hasJustifications = justifications.length > 0;
                                                        const showNoWeekHoursPattern = (attendanceForDay?.has_active_contract && !weekHours && shiftType !== 'senza_calendario' && shiftType !== 'a_chiamata');

                                                        return (
                                                            <td
                                                                key={i}
                                                                className={`border-2 border-gray-200 text-center w-24 text-sm font-semibold uppercase p-1.5 ${attendanceForDay ? 'cursor-pointer' : ''}`}
                                                                onClick={() => {
                                                                    if (shiftType === 'senza_calendario') {
                                                                        Swal.fire({
                                                                            title: 'Tipologia contratto senza calendario',
                                                                            html: `La tipologia contratto è 'Senza calendario' e non è possibile gestire i giustificativi.<br /> Vai sulla <a href="/dipendenti/${dipendente.cf}/edit?tab=2" style="color: #4338ca; text-decoration: underline;">MODIFICA CONTRATTO</a> per aggiornare.`,
                                                                            icon: 'warning',
                                                                            confirmButtonText: 'Ok',
                                                                        });
                                                                    } else if (shiftType !== 'senza_calendario' && shiftType !== 'a_chiamata' && !weekHours) {
                                                                        Swal.fire({
                                                                            title: 'Orario settimanale mancante',
                                                                            html: `Il dipendente non ha orario lavorativo settimanale.<br /> Vai su <a href="/dipendenti/${dipendente.cf}/edit?tab=2" style="color: #4338ca; text-decoration: underline;">MODIFICA CONTRATTO</a> per salvare l'orario.`,
                                                                            icon: 'info',
                                                                            confirmButtonText: 'Ok',
                                                                        });
                                                                    } else {
                                                                        if (profile?.customer_type === 'employee') {
                                                                            const hasRequiredPermission =
                                                                                profile.employee?.permissions?.includes('hr.view_shifts_ou') ||
                                                                                profile.employee?.permissions?.includes('hr.view_shifts_all') ||
                                                                                profile.employee?.permissions?.includes('hr.manage_shifts_ou') ||
                                                                                profile.employee?.permissions?.includes('hr.manage_shifts_all');
                                                                
                                                                            if (!hasRequiredPermission) {
                                                                                Swal.fire({
                                                                                    title: 'Non hai i permessi per eseguire questa azione.',
                                                                                    icon: 'warning',
                                                                                    confirmButtonText: 'Ok',
                                                                                });
                                                                                return;
                                                                            }
                                                                        }
                                                                
                                                                        attendanceForDay && handleOpenDetailsModal(dipendente, formattedDay);
                                                                    }
                                                                }}
                                                                style={{
                                                                    position: 'relative',
                                                                    paddingBottom: '3.2rem',
                                                                    ...((hasActiveContract && isHoliday && weekHours && shiftType !== 'senza_calendario') ? SvgHoliday() : {}),
                                                                    ...((hasActiveContract && isHoliday && weekend && weekHours && shiftType !== 'senza_calendario') ? SvgHolidayWeekend() : {}),
                                                                    ...((hasActiveContract && weekend && weekHours && shiftType !== 'senza_calendario') ? SvgPattern() : {}),
                                                                    ...((hasActiveContract && shiftType === 'senza_calendario') && PatternNoCalendar()),
                                                                    ...(showNoWeekHoursPattern && PatternNoWeekHours()),
                                                                }}
                                                            >
                                                                <div
                                                                    className={`mb-1 mx-auto align-top flex justify-center top-2 absolute left-0 right-0 ${!['0', '-'].includes(hours)  ? 'bg-emerald-100' : ''}`}
                                                                    style={{
                                                                        minWidth: '90%',
                                                                        position: 'absolute',
                                                                        top: '5px',
                                                                        width: '90%',
                                                                        display: hours !== '0' ? 'flex' : 'none',
                                                                        justifyContent: 'center',
                                                                        backgroundColor: !['0', '-'].includes(hours)  ? '#d1fae5' : 'transparent',
                                                                        ...(!hasActiveContract && PatternInactiveEmployee()),
                                                                    }}
                                                                >
                                                                    
                                                                    <span className="px-3">{hours !== '-' ? `${hours}` : <span className='text-transparent'>-</span>}</span>
                                                                    
                                                                </div>

                                                                {hasJustifications && (
                                                                    <div className='flex flex-col' style={{ marginTop: '1.5rem' }}>
                                                                        {orderedJustifications.map((justificationsOfType, index) => {
                                                                            if (justificationsOfType.length > 0) {
                                                                                const totalHours = justificationsOfType.reduce((sum, j) => sum + j.duration, 0) / 60;
                                                                                let formattedTotalHours = Number.isInteger(totalHours) ? totalHours.toFixed(0) : totalHours.toFixed(2);
                                                                                if (formattedTotalHours.endsWith('.00')) {
                                                                                    formattedTotalHours = totalHours.toFixed(0);
                                                                                }
                                                                                
                                                                                const backgroundColor = justificationsOfType.some(j => j.status === 'approved') ? getBackgroundColor(justificationsOfType[0].code) : '';
                                                                                const description = justificationsOfType[0]?.description || justificationsOfType[0]?.code;
                                                                                const hasOuPair = justificationsOfType.some(j => j.ou);

                                                                                return (
                                                                                    <CustomTooltip 
                                                                                        key={`${justificationsOfType[0]?.code}-${index}`} 
                                                                                        title={hasOuPair ? justificationsOfType[0]?.ou_name : ''} 
                                                                                        placement="top-start"
                                                                                        onClick={(e) => {
                                                                                            const formattedDay = new Date(attendanceForDay.date); 

                                                                                            // if (isDayBlocked(formattedDay)) {
                                                                                            //     Swal.fire({
                                                                                            //         icon: 'error',
                                                                                            //         title: 'Il giorno è bloccato',
                                                                                            //         text: 'Modifiche non consentite.',
                                                                                            //         showConfirmButton: true,
                                                                                            //         confirmButtonText: 'OK',
                                                                                            //     });
                                                                                            //     return;
                                                                                            // }

                                                                                            e.stopPropagation();
                                                                                            if (justificationsOfType.length === 1) {
                                                                                                handleJustificationSelected(
                                                                                                    description, 
                                                                                                    dipendente, 
                                                                                                    attendanceForDay, 
                                                                                                    justificationsOfType[0].id, 
                                                                                                    justificationsOfType[0].status
                                                                                                );
                                                                                            } else {
                                                                                                handleShowJustificationsOfType(
                                                                                                    justificationsOfType[0].code, 
                                                                                                    justificationsOfType, 
                                                                                                    description,
                                                                                                    dipendente, 
                                                                                                    attendanceForDay
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        <span
                                                                                            className={`my-0.5 cursor-pointer relative w-full text-gray-700 flex items-center justify-center 
                                                                                                ${formattedTotalHours === '0' ? 'bg-white border border-y border-gray-200 shadow-md' : ''}`}
                                                                                            style={{ backgroundColor: backgroundColor || 'white' }}
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();

                                                                                                const formattedDay = new Date(attendanceForDay.date); 

                                                                                                // if (isDayBlocked(formattedDay)) {
                                                                                                //     Swal.fire({
                                                                                                //         icon: 'error',
                                                                                                //         title: 'Il giorno è bloccato',
                                                                                                //         text: 'Modifiche non consentite.',
                                                                                                //         showConfirmButton: true,
                                                                                                //         confirmButtonText: 'OK',
                                                                                                //     });
                                                                                                //     return;
                                                                                                // }
                                                                                                
                                                                                                if (justificationsOfType.length === 1) {
                                                                                                    handleJustificationSelected(
                                                                                                        description, 
                                                                                                        dipendente, 
                                                                                                        attendanceForDay, 
                                                                                                        justificationsOfType[0].id, 
                                                                                                        justificationsOfType[0].status
                                                                                                    );
                                                                                                } else {
                                                                                                    handleShowJustificationsOfType(
                                                                                                        justificationsOfType[0].code, 
                                                                                                        justificationsOfType, 
                                                                                                        description,
                                                                                                        dipendente, 
                                                                                                        attendanceForDay
                                                                                                    );
                                                                                                }
                                                                                            }}
                                                                                        >
                                                                                            <span className="text-center flex-1">{formattedTotalHours !== '0' ? formattedTotalHours : '-'}</span>

                                                                                            {hasOuPair && (
                                                                                                <div 
                                                                                                    className="absolute top-0 right-0"
                                                                                                    style={{
                                                                                                        width: 0,
                                                                                                        height: 0,
                                                                                                        borderLeft: '10px solid transparent',
                                                                                                        borderTop: '10px solid gray',
                                                                                                    }}
                                                                                                />
                                                                                            )}
                                                                                        </span>
                                                                                    </CustomTooltip>
                                                                                );
                                                                            } else {
                                                                                // Spazio vuoto per giustificativi mancanti
                                                                                return <span key={`empty-${index}`} className="my-0.5 text-gray-400">&nbsp;</span>;
                                                                            }
                                                                        })}
                                                                    </div>
                                                                )}


                                                                {/*                                                             
                                                                {hasOuPair && (
                                                                    <CustomTooltip title={justificationsOfType[0]?.ou_name} placement="top-start">
                                                                        <BuildingOffice2Icon className="w-4 h-4 text-gray-600 absolute right-1" />
                                                                    </CustomTooltip>
                                                                )} */}


                                                                {/* Button to add justification */}
                                                                {canAddJustification && (
                                                                    <div key={i}>
                                                                        <div
                                                                            className="bottom-0 cursor-pointer border-t bg-white"
                                                                            style={{ position: 'absolute', bottom: '0', right: '0', width: '100%', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                                                                            title='Aggiungi giustificativo'
                                                                        >   
                                                                            {attendanceForDay && !isDayBlocked(new Date(formattedDay)) && (
                                                                                <DocumentPlusIcon
                                                                                    className="w-full h-4 m-1 text-gray-400 hover:bg-indigo-50 hover:text-indigo-500"
                                                                                    onClick={(e) => {

                                                                                        // if (isDayBlocked(new Date(formattedDay))) {
                                                                                        //     Swal.fire({
                                                                                        //         icon: 'error',
                                                                                        //         title: 'Giorno bloccato',
                                                                                        //         text: 'Non puoi aggiungere giustificativi su questo giorno perché appartiene a un mese bloccato.',
                                                                                        //         showConfirmButton: true,
                                                                                        //         confirmButtonText: 'OK',
                                                                                        //     });
                                                                                        //     return;
                                                                                        // }

                                                                                        e.stopPropagation();
                                                                                        const requiredPermissions = ['hr.manage_justification_all', 'hr.manage_justification_ou'];
                                                                                        const hasPermission = profile.customer_type !== 'employee' || 
                                                                                            requiredPermissions.some(permission => profile.employee.permissions.includes(permission));
                                                                                        
                                                                                        if (!hasPermission) {
                                                                                            Swal.fire({
                                                                                                icon: 'warning',
                                                                                                title: 'Non hai i permessi per eseguire questa azione',
                                                                                                showConfirmButton: true,
                                                                                                confirmButtonText: 'OK',
                                                                                            });
                                                                                        } else {
                                                                                            openModalAddGiustificativo(dipendente, formattedDay, attendanceForDay);
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </td>
                                                        );
                                                    })}

                                                    {Array.from({ length: emptyCellsAtEnd }).map((_, index) => (
                                                        <td key={`empty-end-${index}`} className="py-3 px-4 border-2 border-gray-200 w-24 bg-gray-50"></td>
                                                    ))}

                                                    {/* COLONNA TOTALI */}
                                                    <td
                                                        style={{
                                                            position: 'sticky',
                                                            right: 0,
                                                            background: 'white',
                                                            zIndex: 1,
                                                            maxWidth: '20%',
                                                            verticalAlign: 'top',
                                                        }}
                                                        className="px-1 text-start text-xs font-semibold bg-gray-50 text-gray-600"
                                                    >
                                                        <div className='px-1 flex flex-col justify-start py-2'>
                                                            {/* Riga con intestazione "ORE PRESENZA" */}
                                                            <div className="flex items-start border-b border-gray-200 pb-0.5">
                                                                <span className="font-bold">PRESENZA</span>
                                                            </div>

                                                            {/* Riga per ore previste */}
                                                            <div className="flex justify-between items-center border-b border-gray-200 py-0.5">
                                                                <span className='pl-4'>Prevista</span>
                                                                <span className='bg-emerald-100 text-gray-900'>
                                                                    {Number.isInteger(totalPresenceHours) ? totalPresenceHours.toFixed(0) : totalPresenceHours.toFixed(2)}
                                                                </span>
                                                            </div>

                                                            {/* Calcolo ore effettive */}
                                                            <div className="flex justify-between items-center border-b border-gray-200 py-0.5">
                                                                <span className='pl-4'>Effettiva</span>
                                                                <span className='bg-emerald-100 text-gray-900'>
                                                                    {Number.isInteger(totalPresenceActualHours) ? totalPresenceActualHours.toFixed(0) : totalPresenceActualHours.toFixed(2)}
                                                                </span>
                                                            </div>

                                                            {/* Visualizzazione dei totali per ogni tipo di giustificativo */}
                                                            {formattedJustificationTotals.map(({ code, hours, description }, index) => {
                                                                const backgroundColor = getBackgroundColor(code);
                                                                return (
                                                                    <div key={index} className="flex justify-between items-center border-b border-gray-200 py-0.5">
                                                                        <span className='pr-2'>{description}</span>
                                                                        <span className='text-gray-900' style={{ backgroundColor: backgroundColor || 'white' }}>
                                                                            {hours !== '0' ? hours : '-'}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        });
                                })()}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {showModalEditGiustificativo && (
                <EditGiustificativoModal
                    selectedJustification={selectedJustification}
                    selectedDescription={selectedDescription}
                    selectedEmployee={selectedEmployee}
                    justificationId={justificationId}
                    closeModalEditGiustificativi={() => setShowModalEditGiustificativo(false)}
                    updateJustification={handleUpdateJustification}
                    onDeleteRequest={handleDeleteRequest}
                    onReject={handleReject}
                />
            )}

            {showModalAddGiustificativo && (
                <AddGiustificativoModal
                    closeModalAddGiustificativi={() => setShowModalAddGiustificativo(false)}
                    dipendente={selectedEmployee}
                    formattedDay={selectedDay}
                    selectedJustification={selectedJustification}
                    onSaveJustificationRequest={handleNewJustificationRequest}
                    selectedUnit={selectedUnit}
                />
            )}

            {showModalJustificationSelect && selectedEmployee && (
                <SelectGiustificativoModal
                    selectedJustification={selectedJustification}
                    selectedDescription={selectedDescription}
                    selectedEmployee={selectedEmployee}
                    selectedJustificationType={selectedJustificationType}
                    justificationsOfType={justificationsOfType}
                    onJustificationSelected={handleJustificationSelected}
                    onClose={() => setShowModalJustificationSelect(false)} 
                />
            )}

            {isModalDetailsOpen && (
                <DayDetailsModal 
                    isOpen={isModalDetailsOpen} 
                    onClose={() => setIsModalDetailsOpen(false)} 
                    formattedDay={modalDetailsData} 
                    employee={selectedEmployee} 
                />
            )}
        </div>
    );
};

export default EmployeeAttendanceWeekTable;

// const SvgPattern = () => {
//     return {
//         backgroundColor: '#ffffff',
//         backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='24' viewBox='0 0 12 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2322c55e' fill-opacity='0.21'%3E%3Cpath d='M2 0h2v12H2V0zm1 20c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM9 8c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zm-1 4h2v12H8V12z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
//         backgroundRepeat: 'repeat',
//     };
// };


// const SvgPattern = () => {
//     return {
//         backgroundColor: '#ffffff',
//         backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='88' height='24' viewBox='0 0 88 24'%3E%3Cg fill-rule='evenodd'%3E%3Cg id='autumn' fill='%2322c55e' fill-opacity='0.13'%3E%3Cpath d='M10 0l30 15 2 1V2.18A10 10 0 0 0 41.76 0H39.7a8 8 0 0 1 .3 2.18v10.58L14.47 0H10zm31.76 24a10 10 0 0 0-5.29-6.76L4 1 2 0v13.82a10 10 0 0 0 5.53 8.94L10 24h4.47l-6.05-3.02A8 8 0 0 1 4 13.82V3.24l31.58 15.78A8 8 0 0 1 39.7 24h2.06zM78 24l2.47-1.24A10 10 0 0 0 86 13.82V0l-2 1-32.47 16.24A10 10 0 0 0 46.24 24h2.06a8 8 0 0 1 4.12-4.98L84 3.24v10.58a8 8 0 0 1-4.42 7.16L73.53 24H78zm0-24L48 15l-2 1V2.18A10 10 0 0 1 46.24 0h2.06a8 8 0 0 0-.3 2.18v10.58L73.53 0H78z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
//         backgroundRepeat: 'repeat',
//     };
// };

const SvgPattern = () => {
    return {
        backgroundColor: '#ffffff',
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='48' height='32' viewBox='0 0 48 32' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2322c55e' fill-opacity='0.13'%3E%3Cpath d='M27 32c0-3.314 2.686-6 6-6 5.523 0 10-4.477 10-10S38.523 6 33 6c-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 6.627 0 12 5.373 12 12s-5.373 12-12 12c-2.21 0-4 1.79-4 4h-2zm-6 0c0-3.314-2.686-6-6-6-5.523 0-10-4.477-10-10S9.477 6 15 6c3.314 0 6-2.686 6-6h-2c0 2.21-1.79 4-4 4C8.373 4 3 9.373 3 16s5.373 12 12 12c2.21 0 4 1.79 4 4h2z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
    };
};

const SvgHoliday = () => {
    return {
        backgroundColor: '#ffffff',
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='48' height='32' viewBox='0 0 48 32' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23fed7aa' fill-opacity='0.4'%3E%3Cpath d='M27 32c0-3.314 2.686-6 6-6 5.523 0 10-4.477 10-10S38.523 6 33 6c-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 6.627 0 12 5.373 12 12s-5.373 12-12 12c-2.21 0-4 1.79-4 4h-2zm-6 0c0-3.314-2.686-6-6-6-5.523 0-10-4.477-10-10S9.477 6 15 6c3.314 0 6-2.686 6-6h-2c0 2.21-1.79 4-4 4C8.373 4 3 9.373 3 16s5.373 12 12 12c2.21 0 4 1.79 4 4h2z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
    };
};


const SvgHolidayWeekend = () => {
    return {
        backgroundColor: '#feead2',
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='48' height='32' viewBox='0 0 48 32' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M27 32c0-3.314 2.686-6 6-6 5.523 0 10-4.477 10-10S38.523 6 33 6c-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 6.627 0 12 5.373 12 12s-5.373 12-12 12c-2.21 0-4 1.79-4 4h-2zm-6 0c0-3.314-2.686-6-6-6-5.523 0-10-4.477-10-10S9.477 6 15 6c3.314 0 6-2.686 6-6h-2c0 2.21-1.79 4-4 4C8.373 4 3 9.373 3 16s5.373 12 12 12c2.21 0 4 1.79 4 4h2z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
    };
};

const PatternInactiveEmployee = () => {
    return {
        backgroundColor: '#ffffff',
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='6' height='6' viewBox='0 0 6 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%234f46e5' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M5 0h1L0 6V5zM6 5v1H5z'/%3E%3C/g%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
    };
};

const PatternNoWeekHours = () => {
    return {
        backgroundColor: '#fffbeb',
        cursor: 'pointer',
    };
};

const PatternNoCalendar = () => {
    return {
        backgroundColor: '#f1f5f9',
        cursor: 'pointer',
    };
};